import React, { useState } from 'react';
import { Button, Alert, Spinner, Card, CardHeader, CardBody } from '../../shim/reactstrap';
import apiClient from '../../api/ApiClient';

export const AdminTickers = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeAction, setActiveAction] = useState(null);
  const [updateIfExists, setUpdateIfExists] = useState(false);
  const [source, setSource] = useState('Polygon');
  const [sources, setSources] = useState([]);

  React.useEffect(() => {
    apiClient.getSources()
      .then(srcs => {
        setSources(srcs);
        if (srcs.length > 0 && !srcs.includes(source)) {
          setSource(srcs[0]);
        }
      })
      .catch(err => console.error('Failed to fetch sources:', err));
  }, [source]);

  const handleCreateStockTickers = async () => {
    if (!window.confirm(`Create stock tickers from ${source} with USD currency?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setActiveAction('stock');

      await apiClient.createTickersBatch({
        Source: source,
        Currencies: ['USD'],
        UpdateIfExists: updateIfExists
      });

      setSuccess('Stock tickers creation initiated! The operation has been queued for processing.');
    } catch (err) {
      console.error('Failed to create stock tickers:', err);
      setError(err.message || 'Failed to create stock tickers');
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  const handleCreateIndicesTickers = async () => {
    if (!window.confirm(`Create indices tickers from ${source}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setActiveAction('indices');

      await apiClient.createTickersBatch({
        Source: source,
        TickerType: 'Index',
        UpdateIfExists: updateIfExists
      });

      setSuccess('Indices tickers creation initiated! The operation has been queued for processing.');
    } catch (err) {
      console.error('Failed to create indices tickers:', err);
      setError(err.message || 'Failed to create indices tickers');
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  const handleCreateETFTickers = async () => {
    if (!window.confirm(`Create ETF tickers from ${source} with USD currency?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setActiveAction('etf');

      await apiClient.createTickersBatch({
        Source: source,
        TickerType: 'ETF',
        Currencies: ['USD'],
        UpdateIfExists: updateIfExists
      });

      setSuccess('ETF tickers creation initiated! The operation has been queued for processing.');
    } catch (err) {
      console.error('Failed to create ETF tickers:', err);
      setError(err.message || 'Failed to create ETF tickers');
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  const handleCreateMutualFundTickers = async () => {
    if (!window.confirm(`Create Mutual Fund tickers from ${source}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setActiveAction('mutualfund');

      await apiClient.createTickersBatch({
        Source: source,
        TickerType: 'MutualFund',
        UpdateIfExists: updateIfExists
      });

      setSuccess('Mutual Fund tickers creation initiated! The operation has been queued for processing.');
    } catch (err) {
      console.error('Failed to create Mutual Fund tickers:', err);
      setError(err.message || 'Failed to create Mutual Fund tickers');
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  const handleCreateFutureTickers = async () => {
    if (!window.confirm(`Create Futures tickers from ${source}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setActiveAction('future');

      await apiClient.createTickersBatch({
        Source: source,
        TickerType: 'Future',
        UpdateIfExists: updateIfExists
      });

      setSuccess('Futures tickers creation initiated! The operation has been queued for processing.');
    } catch (err) {
      console.error('Failed to create Futures tickers:', err);
      setError(err.message || 'Failed to create Futures tickers');
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  const handlePullStaticDetails = async () => {
    if (!window.confirm(`Pull static details from ${source}?`)) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      setActiveAction('static');

      await apiClient.pullStaticDetails({
        Source: source
      });

      setSuccess('Static details pull initiated successfully! The operation has been queued for processing.');
    } catch (err) {
      console.error('Failed to pull static details:', err);
      setError(err.message || 'Failed to pull static details');
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">Tickers Management</h2>
      </div>

      <Alert color="danger" className="mb-3" isOpen={!!error} toggle={() => setError(null)} fade={false}>
        <strong>Error:</strong> {error}
      </Alert>

      <Alert color="success" className="mb-3" isOpen={!!success} toggle={() => setSuccess(null)} fade={false}>
        <strong>Success:</strong> {success}
      </Alert>

      <div className="mb-3">
        <div className="form-group mb-3">
          <label htmlFor="sourceSelect" className="form-label">
            <strong>Data Source</strong>
          </label>
          <select
            id="sourceSelect"
            className="form-control"
            style={{ maxWidth: "300px" }}
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            {sources.map((src) => (
              <option key={src} value={src}>
                {src}
              </option>
            ))}
          </select>
          <small className="form-text text-muted">
            Select the data source for ticker operations
          </small>
        </div>

        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            id="updateIfExists"
            checked={updateIfExists}
            onChange={(e) => setUpdateIfExists(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="updateIfExists">
            Update existing tickers (if they already exist)
          </label>
        </div>
      </div>

      <div className="row">
        <div className="col-md-4 mb-3">
          <Card>
            <CardHeader>
              <h5 className="mb-0">Create Stock Tickers</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted">
                Create stock tickers from {source} data source with USD currency.
              </p>
              <Button
                color="primary"
                block
                onClick={handleCreateStockTickers}
                disabled={loading}
              >
                {loading && activeAction === 'stock' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create Stock Tickers'
                )}
              </Button>
            </CardBody>
          </Card>
        </div>

        <div className="col-md-4 mb-3">
          <Card>
            <CardHeader>
              <h5 className="mb-0">Create Indices Tickers</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted">
                Create indices tickers from {source} data source.
              </p>
              <Button
                color="primary"
                block
                onClick={handleCreateIndicesTickers}
                disabled={loading}
              >
                {loading && activeAction === 'indices' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create Indices Tickers'
                )}
              </Button>
            </CardBody>
          </Card>
        </div>

        <div className="col-md-4 mb-3">
          <Card>
            <CardHeader>
              <h5 className="mb-0">Create ETF Tickers</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted">
                Create ETF tickers from {source} data source with USD currency.
              </p>
              <Button
                color="primary"
                block
                onClick={handleCreateETFTickers}
                disabled={loading}
              >
                {loading && activeAction === 'etf' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create ETF Tickers'
                )}
              </Button>
            </CardBody>
          </Card>
        </div>

        <div className="col-md-4 mb-3">
          <Card>
            <CardHeader>
              <h5 className="mb-0">Create Mutual Fund Tickers</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted">
                Create Mutual Fund tickers from {source} data source.
              </p>
              <Button
                color="primary"
                block
                onClick={handleCreateMutualFundTickers}
                disabled={loading || (source !== 'MarketWatch' && source !== 'FastTrack')}
              >
                {loading && activeAction === 'mutualfund' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create Mutual Fund Tickers'
                )}
              </Button>
              {source !== 'MarketWatch' && (
                <small className="text-muted d-block mt-2">
                  Only available for MarketWatch source
                </small>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="col-md-4 mb-3">
          <Card>
            <CardHeader>
              <h5 className="mb-0">Create Futures Tickers</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted">
                Create futures tickers from {source} data source. Collected tickers
                are prefixed with <code>FUTURE:</code> so they can be distinguished
                from common stocks carrying the same symbol.
              </p>
              <Button
                color="primary"
                block
                onClick={handleCreateFutureTickers}
                disabled={loading || source !== 'MarketWatch'}
              >
                {loading && activeAction === 'future' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Creating...
                  </>
                ) : (
                  'Create Futures Tickers'
                )}
              </Button>
              {source !== 'MarketWatch' && (
                <small className="text-muted d-block mt-2">
                  Only available for MarketWatch source
                </small>
              )}
            </CardBody>
          </Card>
        </div>

        <div className="col-md-4 mb-3">
          <Card>
            <CardHeader>
              <h5 className="mb-0">Pull Static Details</h5>
            </CardHeader>
            <CardBody>
              <p className="text-muted">
                Pull static details for tickers from {source} data source.
              </p>
              <Button
                color="primary"
                block
                onClick={handlePullStaticDetails}
                disabled={loading}
              >
                {loading && activeAction === 'static' ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Pulling...
                  </>
                ) : (
                  'Pull Static Details'
                )}
              </Button>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
};
